<template>
	<div class="container container--sm">
		<div v-if="acceptOnlineOrder" class="cart-wrap">
			<page-header :title="venueInfo.name" @go-back="goToProducts">
				<template v-slot:rightButton>
					<icon-svg
						icon="ic_delete_cart"
						class="remove-cart-btn"
						:height="19"
						:width="18"
						color="none"
						@click="isConfirmModalOpen = true"
					/>
				</template>
			</page-header>
			<transition name="message">
				<cart-info-message
					v-if="cartInfoMessage"
					:message="cartInfoMessage"
					@hide="hideInfoMessage"
				/>
			</transition>
			<div class="cart-frame">
				<time-view class="mb-12" />
				<template v-if="isTabOpened">
					<div>
						<div
							v-if="cartItemsBeyondOrder.length"
							class="products-group-title"
						>
							New
						</div>
						<cart-item
							v-for="item in groupedCartItemsBeyondOrder"
							:key="item.id"
							:item="item"
							:deleting-cart-item-ids="deletingCartItemIds"
							@remove="callDeleteCartItem"
						/>
					</div>

					<div>
						<div v-if="cartItemsInOrder.length" class="products-group-title">
							In Order
						</div>
						<cart-item
							v-for="item in groupedCartItemsInOrder"
							:key="item.id"
							:item="item"
							:deleting-cart-item-ids="deletingCartItemIds"
							in-order
							@show-product-delete-popup="showProductDeletePopup($event, item)"
							@remove="callDeleteCartItem"
						/>
					</div>
				</template>

				<template v-else>
					<cart-item
						v-for="item in groupedCartItems"
						:key="item.id"
						:item="item"
						:deleting-cart-item-ids="deletingCartItemIds"
						@remove="callDeleteCartItem"
					/>
				</template>

				<div v-if="showAdditionalInstruction" class="instructions">
					<auto-resize-textarea
						:value="currentCartNotice"
						placeholder="Order Instructions"
						:symbols-limit="300"
						@on-change="changeCartNotice"
					/>
				</div>
			</div>

			<cart-footer
				:is-loading="isLoading"
				:before-checkout="updateVenueInfo"
				ref="cartFooter"
				@set-is-loading="setIsLoading"
				@delete-order="clearCart"
			/>
			<edit-product-details-popup @update-cart="callGetCart" />
			<cart-product-details-sidebar
				:product-id="addingProductId"
				:before-adding-action="callDeleteCartItemByModal"
				:initial-products-count="selectedInOrderProductCount"
				@close="isAddingSidebarOpen = false"
			/>

			<loader v-if="isLoading" />
			<transition name="fade">
				<remove-cart-confirmation-modal
					v-if="isConfirmModalOpen"
					dark-theme
					:first-button-action="closeConfirmPopup"
					:second-button-action="clearCart"
					:is-loading="isLoading"
				/>
			</transition>

			<transition name="fade">
				<unavailability-info-modal
					v-if="isUnavailabilityModalOpen"
					title="You Cannot Edit This Product"
					description="Contact staff for help"
					dark-theme
					:first-button-action="closeUnavailabilityModal"
					@close="closeUnavailabilityModal"
				/>
			</transition>
		</div>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import debounce from 'lodash/debounce'
import { groupBy } from 'lodash'

import PageHeader from '@/components/PageHeader'
import CartInfoMessage from './CartInfoMessage'
import CartItem from '@/components/CartItem'
import CartFooter from './CartFooter'
import EditProductDetailsPopup from '@/components/ProductDetails/EditProductDetailsPopup/EditProductDetailsPopup.vue'
import CartProductDetailsSidebar from '@/components/ProductDetails/CartProductDetailsSidebar.vue'
import Loader from '@/components/Loaders/Loader.vue'
import TimeView from '@/components/TimeView'
import IconSvg from '@/components/IconSvg.vue'
import UnavailabilityInfoModal from '@/components/modals/UnavailabilityInfoModal.vue'

import {
	ORDER_LIMITATION_CONDITION,
	PAYMENT_PROVIDER,
	PRODUCT_ORDER_TYPES,
	VALIDATION_MESSAGES
} from '@/constants'
import RemoveCartConfirmationModal from '@/components/modals/RomoveCartConfirmationModal.vue'
import routerMixin from '@/mixins/routerMixin'
import { getValidationCode } from '@/utils/helpers'
import storage from '@/utils/storage'
import AutoResizeTextarea from '@/components/AutoResizeTextarea/AutoResizeTextarea.vue'

export default {
	name: 'Cart',

	mixins: [routerMixin],

	components: {
		UnavailabilityInfoModal,
		RemoveCartConfirmationModal,
		IconSvg,
		PageHeader,
		CartInfoMessage,
		CartItem,
		CartFooter,
		EditProductDetailsPopup,
		CartProductDetailsSidebar,
		Loader,
		TimeView,
		AutoResizeTextarea
	},

	PP_MAGENSA: PAYMENT_PROVIDER.MAGENSA,

	data() {
		return {
			currentCartNotice: '',
			isShowQrCodeScanner: false,
			isShowCreditCard: false,
			infoMessage: null,
			isInfoMessageVisible: false,
			deletingCartItemIds: [],
			isLoading: false,
			isConfirmModalOpen: false,
			selectedProductHash: null,
			selectedInOrderProductId: null,
			isAddingSidebarOpen: false,
			isProductRemoving: false,
			isUnavailabilityModalOpen: false,
			selectedInOrderProductCount: 1
		}
	},

	async created() {
		if (!this.acceptOnlineOrder) {
			return await this.goToProducts()
		}

		await Promise.all([this.updateVenueInfo(), this.callGetCart()])

		this.checkIsOrderCorrect()

		this.currentCartNotice = this.cartNotice
	},

	mounted() {
		if (this.caPaymentProvider === this.$options.PP_MAGENSA) {
			this.mountTEconnect()
		}
	},

	computed: {
		...mapState('venue', ['venueInfo', 'pickUpHours']),
		...mapState({
			cart: state => state.cart
		}),
		...mapState('cart', [
			'creditCardId',
			'cartItems',
			'cartInfoMessage',
			'orderType',
			'includeUtensils'
		]),
		...mapGetters('cart', [
			'cartHasFood',
			'cartHasBeverage',
			'isCartOrderTypePickUp',
			'isCartOrderTypeInVenue',
			'isToGoProductsIncludesFood',
			'isTabOpened',
			'cartItemsInOrder',
			'cartItemsBeyondOrder'
		]),
		...mapGetters('venue', ['isOrderingTimeAvailable', 'caPaymentProvider']),
		...mapGetters('payment', ['magensaPaymentInstance', 'tecPrInterface']),

		addingProductId() {
			return (this.isAddingSidebarOpen && this.selectedInOrderProductId) || ''
		},

		venueId() {
			return this.venueInfo.id
		},

		cartId() {
			return this.cart.id
		},

		cartNotice() {
			return this.cart.notice
		},

		acceptOnlineOrder() {
			return this.venueInfo.acceptOnlineOrder
		},

		venueSettings() {
			return this.venueInfo.settings
		},

		showAdditionalInstruction() {
			const { acceptRoundNotice: notice } = this.venueSettings
			return notice === true || notice === false ? notice : true
			// enable if acceptRoundNotice not presented in venue info (venue chain <3.5)
		},

		groupedCartItems() {
			return this.getGroupedItems(this.cartItems)
		},

		groupedCartItemsInOrder() {
			return this.getGroupedItems(this.cartItemsInOrder)
		},

		groupedCartItemsBeyondOrder() {
			return this.getGroupedItems(this.cartItemsBeyondOrder)
		}
	},

	methods: {
		...mapActions('venue', ['setAllOrderingAvailable']),
		...mapActions('venue', ['getVenueInfo']),
		...mapActions('cart', [
			'updateCart',
			'removeProducts',
			'getCart',
			'setCartInfoMessage',
			'deleteCartItem',
			'closeTab',
			'toggleIsCartRecreated'
		]),

		...mapActions('payment', ['getCreditCards', 'mountTEconnect']),

		...mapActions('ui', ['addMainError']),

		closeUnavailabilityModal() {
			this.isUnavailabilityModalOpen = false
		},

		getGroupedItems(items) {
			const groupedByHash = groupBy(items, 'similarityHash')

			return Object.keys(groupedByHash).map(hash => ({
				...groupedByHash[hash][0],
				quantity: groupedByHash[hash].reduce(
					(sum, item) => sum + item.quantity,
					0
				)
			}))
		},

		showProductDeletePopup(similarityHash, item) {
			this.selectedProductHash = similarityHash

			if (!item.productId) {
				this.isUnavailabilityModalOpen = true

				return
			}

			this.selectedInOrderProductId = item.productId
			this.selectedInOrderProductCount = item.quantity
			this.isAddingSidebarOpen = true
		},

		async callDeleteCartItemByModal() {
			try {
				this.isProductRemoving = true

				const data = {
					cartId: this.cartId,
					similarityHash: this.selectedProductHash
				}

				try {
					await this.deleteCartItem(data)
				} catch (e) {
					const validationCode = getValidationCode(e)

					if (validationCode === VALIDATION_MESSAGES.CART_NOT_FOUND) {
						this.toggleIsCartRecreated()
					}

					throw e
				}
			} finally {
				this.isProductRemoving = false
			}
		},

		getVenueData() {
			const { subDomain, venueChainId } = this.$route.params

			return {
				venueChainSubdomain: subDomain,
				venueId: venueChainId
			}
		},

		async updateVenueInfo() {
			const venueData = this.getVenueData()

			await this.getVenueInfo(venueData)

			this.setAllOrderingAvailable()
		},

		showInfoMessage(message) {
			this.setCartInfoMessage(message)
		},

		hideInfoMessage() {
			this.setCartInfoMessage(null)
		},

		callDeferredUpdateCart: debounce(async function() {
			this.callUpdateCart()
		}, 300),

		changeCartNotice(text) {
			this.currentCartNotice = text
			this.callDeferredUpdateCart()
		},

		checkIsOrderCorrect() {
			if (!this.cartItems.length) return

			const {
				orderLimCondition,
				orderLimForHere,
				orderLimPickUp
			} = this.venueInfo.settings

			const isForHereProducts = this.cartItems.some(
				item => item.orderType === PRODUCT_ORDER_TYPES.forHere
			)
			const isPickUpProducts = this.cartItems.some(
				item => item.orderType === PRODUCT_ORDER_TYPES.pickUp
			)

			const isMandatoryCheckNeeded =
				(orderLimForHere && orderLimPickUp) ||
				(orderLimForHere && isForHereProducts) ||
				(orderLimPickUp && isPickUpProducts)

			let message = null

			if (isMandatoryCheckNeeded) {
				switch (orderLimCondition) {
					case ORDER_LIMITATION_CONDITION.ORDER_HAS_AT_LIST_ONE_FOOD:
						if (!this.cartHasFood) {
							message = {
								title: 'Your order must include',
								subTitle: 'At least 1 food item'
							}
						}
						break
					case ORDER_LIMITATION_CONDITION.ORDER_HAS_AT_LIST_ONE_DRINK:
						if (!this.cartHasBeverage) {
							message = {
								title: 'Your order must include',
								subTitle: 'At least 1 drink item'
							}
						}
						break
					default:
						message = null
				}
			}

			this.showInfoMessage(message)
		},

		async callGetCart(isNeedShowExpiredPopup = false) {
			try {
				this.isLoading = true

				await this.getCart(isNeedShowExpiredPopup)
			} finally {
				this.isLoading = false
			}
		},

		async callUpdateCart() {
			try {
				await this.updateCart({
					notice: this.currentCartNotice,
					isNeedShowExpiredPopup: true
				})
			} catch (e) {}
		},

		async callDeleteCartItem(similarityHash) {
			this.isProductRemoving = true

			try {
				this.deletingCartItemIds.push(similarityHash)
				const data = {
					cartId: this.cartId,
					similarityHash
				}

				try {
					await this.deleteCartItem(data)
				} catch (e) {
					const validationCode = getValidationCode(e)

					if (validationCode === VALIDATION_MESSAGES.CART_NOT_FOUND) {
						this.toggleIsCartRecreated()

						return
					}
				}

				this.deletingCartItemIds = this.deletingCartItemIds.filter(
					similarityHash => similarityHash !== similarityHash
				)

				if (!this.cartItems.length) {
					if (this.isTabOpened) {
						await this.closeTab({ cartId: this.cartId })
						storage.removeFromStorage('cartId')
					}

					await this.callGetCart()
					await this.goToProducts()
				}
			} catch (e) {
				const errorStatus = e && e.response && e.response.status

				if (errorStatus === 400) {
					await this.callGetCart(true)
				}
				this.deletingCartItemIds = this.deletingCartItemIds.filter(
					similarityHash => similarityHash !== similarityHash
				)
			} finally {
				this.isProductRemoving = false
			}
		},

		closeConfirmPopup() {
			this.isConfirmModalOpen = false
		},

		async clearCart() {
			if (!this.cartId) return

			try {
				this.isLoading = true

				if (this.isTabOpened) {
					await this.closeTab({ cartId: this.cartId })
					storage.removeFromStorage('cartId')
				} else {
					await this.removeProducts(this.cartId)
				}

				await this.callGetCart()
				await this.goToProducts()
			} catch (e) {
				this.isLoading = false
				this.getCart()
			}
		},

		setIsLoading(val) {
			this.isLoading = val
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.cart-wrap {
	padding-bottom: 60px;
	.header {
		flex-shrink: 0;
		z-index: 10;
	}

	.products-group-title {
		margin-bottom: 16px;
		color: $white;
		font-size: 16px;
		line-height: 22px;
	}

	.remove {
		height: 100%;
		width: 80px;
		padding: 0 26px;
		background: #e9576a url('../../assets/images/ic_remove.svg') no-repeat 50%;
		border: 0;
		cursor: pointer;
	}

	.back-link {
		cursor: pointer;
		text-decoration: underline;
		font-weight: 700;
		color: rgba(255, 255, 255, 0.5);
	}

	.page-header {
		.page-header-right-button {
			padding: 9px 0;
			cursor: pointer;
			opacity: 1;
			transition: opacity 0.3s;
			&:hover {
				opacity: 0.7;
			}
		}
		.back-button {
			opacity: 1;
			transition: opacity 0.3s;
			transform: translateX(-8px);
			&:hover {
				opacity: 0.7;
			}
		}
	}
}

.cart-frame {
	padding: 20px 0 40px;
	position: relative;
	overflow-y: auto;
}

.open-state {
	margin: 0 auto 15px 0;
}

@media (max-width: 767px) {
	.cart-frame {
		padding: 20px 0;
	}
}
</style>
